"use strict";
/**
 * AI Model Configuration - Single Source of Truth
 *
 * This file defines available AI models for the application.
 * Frontend, backend, and infrastructure all import from here.
 *
 * MAINTENANCE:
 * - To update models: Change this file only, then deploy
 * - MODEL_TIERS: The Bedrock model IDs used at runtime
 * - AVAILABLE_MODELS: The UI-facing model list (dropdown, selector)
 * - MODE_DEFAULT_MODELS: Which model each AI mode defaults to
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_MODEL_ID = exports.MODE_DEFAULT_MODELS = exports.AVAILABLE_MODELS = exports.MODEL_TIERS = void 0;
exports.getModelById = getModelById;
exports.getDefaultModel = getDefaultModel;
exports.isValidModelId = isValidModelId;
/**
 * Model tier constants for auto-routing
 * Used by model-router.ts to select appropriate model based on task complexity
 *
 * NOTE: Using US geographic inference profiles (us.anthropic.*) for:
 * - US data residency (data stays in US regions)
 * - Higher throughput (routes to optimal US region)
 * - Claude 4.x requirement (these models only support inference profiles)
 *
 * Opus 4.6 released Feb 5, 2026 - model ID simplified (no date suffix)
 * Sonnet 4.6 released Feb 17, 2026 - same simplified ID pattern
 *
 * VPC endpoint privateDnsEnabled is disabled to allow cross-region routing.
 * Traffic still stays on AWS backbone (encrypted), just routes via NAT.
 */
exports.MODEL_TIERS = {
    HAIKU: 'us.anthropic.claude-haiku-4-5-20251001-v1:0',
    SONNET: 'us.anthropic.claude-sonnet-4-6',
    OPUS: 'us.anthropic.claude-opus-4-6-v1',
};
/**
 * Available AI models for Aerotage platform
 * All models use us.anthropic prefix for US geographic inference (US data residency)
 */
exports.AVAILABLE_MODELS = [
    {
        id: 'auto',
        name: 'Auto (Smart)',
        description: 'Automatically selects the best model based on task complexity (cost-conscious)',
        provider: 'bedrock',
        capabilities: {
            vision: true,
            contextWindow: 200000,
            speed: 'balanced',
        },
    },
    {
        id: exports.MODEL_TIERS.OPUS,
        name: 'Claude Opus 4.6',
        description: 'Most powerful for agentic AI, autonomous coding, complex reasoning, and mission-critical tasks',
        provider: 'bedrock',
        capabilities: {
            vision: true,
            contextWindow: 200000,
            speed: 'thorough',
        },
    },
    {
        id: exports.MODEL_TIERS.SONNET,
        name: 'Claude Sonnet 4.6',
        description: 'Balanced model for complex analysis, certification work, and document editing',
        provider: 'bedrock',
        capabilities: {
            vision: true,
            contextWindow: 200000,
            speed: 'balanced',
        },
    },
    {
        id: exports.MODEL_TIERS.HAIKU,
        name: 'Claude Haiku 4.5',
        description: 'Fast, cost-effective model for simple queries and quick searches',
        provider: 'bedrock',
        capabilities: {
            vision: true,
            contextWindow: 200000,
            speed: 'fast',
        },
    },
];
/**
 * Per-mode default models — all modes default to 'auto' so the backend
 * auto-router (model-router.ts) is the single decision-maker.
 * The router handles mode-specific routing (plan→Opus, diagram→Sonnet, etc.)
 * and respects env toggles (FORCE_OPUS_FOR_AGENT, FORCE_OPUS_FOR_DEVELOP).
 */
exports.MODE_DEFAULT_MODELS = {
    ask: 'auto',
    agent: 'auto',
    diagram: 'auto',
    plan: 'auto',
    develop: 'auto',
};
/**
 * Default model ID - 'auto' enables smart model selection
 * Backend will route to appropriate model based on task complexity
 */
exports.DEFAULT_MODEL_ID = 'auto';
/**
 * Get model by ID
 */
function getModelById(modelId) {
    return exports.AVAILABLE_MODELS.find(m => m.id === modelId);
}
/**
 * Get default model
 */
function getDefaultModel() {
    return exports.AVAILABLE_MODELS.find(m => m.id === exports.DEFAULT_MODEL_ID) || exports.AVAILABLE_MODELS[0];
}
/**
 * Check if a model ID is valid (exists in AVAILABLE_MODELS).
 * Used to detect stale model IDs persisted from previous deployments.
 */
function isValidModelId(modelId) {
    return exports.AVAILABLE_MODELS.some(m => m.id === modelId);
}
//# sourceMappingURL=model-config.js.map